/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback } from "react";
import DangerousContent from "~/common/DangerousContent";
import ModalReact, { Body, Header } from "~/common/ModalReact";
import connectUser, { User } from "~/common/Store/connectUserAndChat";

import CustomizablePageHeader from "./CustomizablePageHeader";
import { CustomizationProvider } from "../useCustomizableContext";

import Customizer from "./Customizer";

import "./customizable.css";

interface Props {
  user: User;
  data: any;
}

const getDesignConsiderations = (
  designConsiderations: string,
  isTrs: boolean
) => {
  let content = designConsiderations || "";

  if (isTrs) {
    const specialOrdersEmail = "specialorders@webstaurantstore.com";
    const customQuoteEmail = "customquote@webstaurantstore.com";
    const customizableEmail = "customizable@webstaurantstore.com";

    const trsEmail = "TRScustomorders@therestaurantstore.com";

    content = content
      .replace(specialOrdersEmail, trsEmail)
      .replace(customQuoteEmail, trsEmail)
      .replace(customizableEmail, trsEmail);
  }

  return content;
};

const CustomizableTool = ({ user, data }: Props) => {
  const [isLoadingCart] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [userData, setUserData] = useState(user);

  const getUserData = useCallback(async () => {
    const response = await fetch("/api:edgecache/getdynamicdata");
    const data = await response.json<{ user: User }>();
    return data.user;
  }, []);

  useEffect(() => {
    if ((!userData?.index || userData.index === "0") && !userData.sessionId) {
      getUserData().then(setUserData);
    }
  }, [userData, getUserData]);

  const getIsTRSIframe = () => {
    const query = new URLSearchParams(window.location.search);
    return Boolean(query.get("is_trs_iframe"));
  };

  const [isTRS] = useState(getIsTRSIframe());

  const {
    productData: { description },
  } = data.items[data.itemNumberId.toString()] || {};

  const designConsiderations = getDesignConsiderations(description, isTRS);

  return (
    <CustomizationProvider data={data}>
      <div id="main-container">
        <div id="app" className="customize__content">
          <CustomizablePageHeader
            isLoadingCart={isLoadingCart}
            setOpenModal={() => setOpenModal(true)}
            isTrs={isTRS}
          />
          <Customizer
            user={userData}
            openModal={() => setOpenModal(true)}
            isLoadingCart={isLoadingCart}
            isTrs={isTRS}
          />
          {!isTRS && (
            <div className="container mt-5 box-content">
              <div className="bg-purple-50 mb-5 overflow-hidden rounded">
                <div className="font-bold bg-gradient-to-r from-[#982FC6] to-purple-800 py-3 lg:hidden text-white text-2xl pl-4">
                  Artwork & File Prep
                </div>
                <div className="flex items-center justify-between gap-1 xl:gap-6">
                  <div className="flex-none xsl:flex-initial lg:flex-none w-2/5 xsl:w-auto">
                    <picture>
                      <source
                        srcSet="
                    https://www.webstaurantstore.com/uploads/design/2023/1/Customizable-Supplies/customizable-file-prep-large.svg
                  "
                        media="(min-width: 1280px)"
                      />
                      <source
                        srcSet="
                    https://www.webstaurantstore.com/uploads/design/2023/1/Customizable-Supplies/customizable-file-prep-medium.svg
                  "
                        media="(min-width: 1024px)"
                      />
                      <img
                        src="https://www.webstaurantstore.com/uploads/design/2023/1/Customizable-Supplies/customizable-file-prep-small.svg"
                        alt="coffee cup"
                        className="max-w-[128%] sm:max-w-full"
                      />
                    </picture>
                  </div>

                  <div className="flex xl:flex-row flex-col lg:justify-between xl:items-center w-full md:gap-5 mr-8 xl:mr-12 xxl:mr-20 my-6 lg:my-2">
                    <div className="flex flex-col md:gap-2 lg:gap-1">
                      <h3 className="text-2xl font-bold leading-none text-purple-800 block mb-2 md:mb-0">
                        Taking the guesswork out of custom printing!
                      </h3>
                      <p className="mb-2 md:mb-0 font-sm leading-none text-purple-800">
                        Send us your artwork and we’ll make sure your products
                        look their best.
                      </p>
                    </div>
                    <div className="mt-4 md:mt-0">
                      <a
                        href="/services/artwork-file-prep/"
                        className="text-white bg-purple-800 px-8 py-2 rounded text-center w-24"
                      >
                        Learn More
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <ModalReact
          isOpen={openModal}
          onRequestClose={() => setOpenModal(false)}
          className="max-w-5xl"
        >
          <Header
            closeModal={() => setOpenModal(false)}
            className="modal-header"
          >
            <span className="modal-title">Design Considerations</span>
          </Header>
          <Body className="modal-content">
            <DangerousContent contentLength={designConsiderations.length}>
              {designConsiderations}
            </DangerousContent>
          </Body>
        </ModalReact>
      </div>
    </CustomizationProvider>
  );
};

export default connectUser(CustomizableTool);
